import { Link, useParams } from "react-router-dom";
import { useApi } from "../lib/api";
import { useSeo, breadcrumbLd } from "../lib/seo";
import { PageHero, EmptyState, Crumbs } from "../components/shop/Common";
import { ProductListing } from "../components/shop/ProductListing";
import { DestinationCard } from "../components/shop/DestinationCard";

const Suggestions = ({ q, title, text }) => {
  const near = useApi("/destinations/search", { q, limit: 4 });
  const feat = useApi("/destinations", { featured: true });
  const list = [...(near.data || []).filter((d) => d.image), ...(feat.data || [])]
    .filter((d, i, a) => a.findIndex((x) => x.slug === d.slug) === i)
    .slice(0, 3);
  return (
    <EmptyState title={title} text={text} testid="destination-empty">
      <div className="grid sm:grid-cols-3 gap-4 text-left">
        {list.map((d) => <DestinationCard key={d.slug} d={d} className="h-72" />)}
      </div>
      <Link to="/shop" className="inline-block mt-8 rounded-full bg-navy text-white px-6 py-3 font-bold" data-testid="shop-general-apparel">Shop general vacation apparel</Link>
    </EmptyState>
  );
};

export default function DestinationPage() {
  const { slug } = useParams();
  const dest = useApi(`/destinations/${slug}`);
  const prods = useApi("/products", { destination: slug });
  const d = dest.data;
  const name = d?.name || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  useSeo({
    title: d ? `${d.name} Vacation Shirts, Hats & Apparel` : `${name} Vacation Apparel`,
    description: d ? `${d.description} Shop funny ${d.name} T-shirts, vacation outfits and hats from Vacation Apparel.` : `Looking for ${name} vacation apparel? Explore similar destinations.`,
    image: d?.image,
    noindex: !d,
    jsonLd: d && { "@context": "https://schema.org", ...breadcrumbLd([["Home", "/"], ["Destinations", "/destinations"], [d.name, `/destinations/${d.slug}`]]) },
  });

  if (dest.loading) return <div className="h-[60vh] bg-navy animate-pulse" />;

  return (
    <>
      <PageHero eyebrow={d ? `${d.region} · ${d.country}` : "Destination"} title={`${name.toUpperCase()} VACATION APPAREL`} subtitle={d?.subtitle || "Your next adventure awaits."} image={d?.image}>
        {d && <p className="mt-4 text-white/80 max-w-xl" data-testid="destination-description">{d.description}</p>}
      </PageHero>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Crumbs items={[["Home", "/"], ["Destinations", "/destinations"], [name]]} />
        {!d ? (
          <Suggestions q={slug.replace(/-/g, " ")} title={`We don't have ${name} designs yet`} text="We're always adding new destinations. In the meantime, check out these popular spots or shop our general vacation apparel." />
        ) : (
          <ProductListing
            products={prods.data}
            loading={prods.loading}
            empty={<Suggestions q={d.name} title={`${d.name} designs are coming soon`} text={`We haven't released our ${d.name} collection yet. Explore these destinations or shop general vacation apparel.`} />}
          />
        )}
      </section>
    </>
  );
}
